import { Component, inject, Input, OnInit, ViewChild } from '@angular/core';
import { FormArray, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Autor, GaudeAutor } from '../../../../model/classes/gaude-autor';
import { AUTOR_GAUDE_DATALIST_NAME } from '../../../../model/contants/datalists';
import { UdeuService } from '../../../../services/udeu.service';
import { AppStore, SnackbarWarningAction } from '@alfresco/aca-shared/store';
import { Store } from '@ngrx/store';
import { UdeuAuthor } from '../../../../model/classes/udeu-record';
import { RecordActionTypes } from '../../../../store/actions/record-actions';
import { MatAutocompleteTrigger } from '@angular/material/autocomplete';

@Component({
  selector: 'alden-authors',
  templateUrl: './authors.component.html',
  styleUrls: ['./authors.component.scss'],
})
export class AuthorsComponent implements OnInit {
  @Input() group: FormGroup<any>;
  @Input() controlName: string;
  @Input() recordActionType: RecordActionTypes; 
  @Input() udeuAuthors: UdeuAuthor[];

  @ViewChild(MatAutocompleteTrigger) autocompleteTrigger!: MatAutocompleteTrigger;
    
  options: Autor[] = [];

  loading: boolean;
  disabled: boolean;
  addAuthorError: string;

  private _formBuilder = inject(FormBuilder);
  formGroup: FormGroup<any>;

  RecordActionTypes: typeof RecordActionTypes = RecordActionTypes;

  constructor(private udeuService: UdeuService, protected store: Store<AppStore>) { }

  ngOnInit(): void {
    this.disabled =this.group.get(this.controlName).disabled;
    this.formGroup = this.createForm();
    if (this.udeuAuthors) {
      this.udeuAuthors.forEach((udeuAuthor) => {
        const form = this.createForm(udeuAuthor);
        this.disabled || udeuAuthor.gaudeId ? form.disable() : null;
        this.formArray.push(form);
      });
    }

    this.formGroup.get('name').valueChanges.subscribe((_value: string) => {
      this.add(false);
    });
    this.formGroup.get('lastname1').valueChanges.subscribe((_value: string) => {
      this.add(false);
    });
    this.formGroup.get('lastname2').valueChanges.subscribe((_value: string) => {
      this.add(false);
    });
    this.formGroup.get('ria').valueChanges.subscribe((_value: string) => {
      this.add(false);
    });
  }

  createForm(autor?: UdeuAuthor): FormGroup<any> {
    return this._formBuilder.group({
      search: ['', [Validators.minLength(3)]],
      id: [autor?.id],
      gaudeId: [{ value: autor?.gaudeId, disabled: true }],
      ria: [autor?.ria],
      name: [autor?.name, [Validators.required]],
      lastname1: [autor?.lastname1],
      lastname2: [autor?.lastname2]
    });
  }

  async searchAuthor() {
    const valueAuthor = this.formGroup.get('search').value;
    if (typeof valueAuthor === 'string' && valueAuthor.length >= 3) {
      this.loading = true;
      this.autocompleteTrigger.closePanel();
      try {
        const results: GaudeAutor = await this.udeuService.getGaudeDataListValues(AUTOR_GAUDE_DATALIST_NAME, valueAuthor);
        this.options = results.items;
        if (this.options?.length === 1) {
          const author = this.options[0];
          this.formGroup.get('search').setValue(author);
          this.addGaudeAuthor(author);
        } else {
          this.autocompleteTrigger.openPanel();
        }
      } catch (err) {
        console.error(err);
        this.store.dispatch(new SnackbarWarningAction('No se pudo obtener los autores.'));
      } finally {
        this.loading = false;
      }
    } else if (valueAuthor.length < 3) {
      this.resetForm();
    }
  }

  addGaudeAuthor(author: Autor) {
    this.formGroup.disable();
    this.formGroup.get('search').enable();

    this.formGroup.get('name').setValue(author.nombre);
    this.formGroup.get('lastname1').setValue(author.apellido1);
    this.formGroup.get('lastname2').setValue(author.apellido2);
    this.formGroup.get('ria').setValue(author.ria);
    this.formGroup.get('gaudeId').setValue(author.id_entidad);
  }

  displayFn(autor: Autor): string {
    return autor ? `${autor.ria} - ${autor.nombre} ${autor.apellido1 ?? ''} ${autor.apellido2 ?? ''}` : '';
  }

  isSelected(option: Autor, controlName: string): boolean {
    const value = this.formGroup.get(controlName).value;
    return value && typeof option !== 'string' ? value === option : false;
  }

  add(add: boolean = true) {
    const author = new UdeuAuthor();
    author.gaudeId = this.formGroup.get('gaudeId').value;
    author.ria = this.formGroup.get('ria').value?.trim().toUpperCase();
    author.name = this.formGroup.get('name').value?.trim().toUpperCase();
    author.lastname1 = this.formGroup.get('lastname1').value?.trim().toUpperCase();
    author.lastname2 = this.formGroup.get('lastname2').value?.trim().toUpperCase();

    const authorOk = this.checkAddAuthor(author)
    if(add && authorOk) {
      const form = this.createForm(author);
      author.gaudeId ? form.disable() : null;
      this.formArray.push(form);
      this.resetForm();
    }
  }

  checkAddAuthor(author: UdeuAuthor) {
    this.addAuthorError = "";

    if(author.gaudeId && this.formArray.getRawValue().some((a: UdeuAuthor) => a.gaudeId === author.gaudeId)) {
      this.addAuthorError = "DEUSTO.MESSAGES.ERRORS.AUTHORS.ENTITY_ID";
      return false;
    }

    if(author.ria) {
      if(this.formArray.getRawValue().some((a: UdeuAuthor) => a.ria === author.ria)) {
        this.addAuthorError = "DEUSTO.MESSAGES.ERRORS.AUTHORS.RIA";
        return false;
      }
    } else {
      if(author.name && this.formArray.getRawValue().some((a: UdeuAuthor) => 
        [(a.name ?? '').trim().toUpperCase(), (a.lastname1 ?? '').trim().toUpperCase(), (a.lastname2 ?? '').trim().toUpperCase()].join(' ') 
        === [(author.name ?? '').trim().toUpperCase(), (author.lastname1 ?? '').trim().toUpperCase(), (author.lastname2 ?? '').trim().toUpperCase()].join(' '))) {
        this.addAuthorError = "DEUSTO.MESSAGES.ERRORS.AUTHORS.NAME";
        return false;
      }
    }

    return true;
  }

  resetForm() {
    this.options = [];
    this.formGroup.enable();
    this.formGroup.get('gaudeId').disable();

    this.formGroup.reset();
    this.formGroup.setErrors(null);
    this.formGroup.markAsUntouched();

    this.addAuthorError = "";
  }

  delete(index: number) {
    const formArray = this.formArray;
    formArray.removeAt(index);
  }

  get formArray(): FormArray {
    return this.group.get(this.controlName) as FormArray;
  }

  formArrayGroup (index: number): FormGroup {
    return this.formArray.at(index) as FormGroup;
  }

}
